import { type FormEvent, useMemo, useState } from "react";
import { useNavigate } from "react-router";

import {
  Badge,
  BadgeColor,
  Button,
  ButtonStyle,
  Checkbox,
  FormField,
  SearchableDropdown,
  type SearchableDropdownListItem,
  Spinner,
} from "@prequel/shared-components";

import { EMPTY_PROVIDER_DRAFT, PROVIDER_ID_MAX_LENGTH, type ProviderDraft, useProviderForm } from "./formState";

import { useAllDatalakes } from "@/api/import/datalakes";
import { SYSTEM_PRODUCT_NAME, useAllImportProducts } from "@/api/import/products";
import { useCreateProvider, useUpdateProvider } from "@/api/import/providers";

const NAME_SUBTEXT =
  "Descriptive name for this Provider. This will only be visible internally and is only used as a reference.";
const ID_SUBTEXT =
  "Provider identifier. This is the ID used to identify the Provider, and will be delivered alongside every record synced downstream.";
const DATALAKE_SUBTEXT =
  "The Datalake used for this Provider's data. All Datasets owned by this provider will be stored in the selected Datalake.";

const PROVIDERS_PATH = "/import/providers";

// Above this many products the plain checkbox list is unusable, so offer a filter.
const PRODUCT_FILTER_THRESHOLD = 10;

type ProviderFormProps = {
  providerId?: string;
  initialDraft?: ProviderDraft;
  cancelPath?: string;
};

const ProviderForm = ({
  providerId,
  initialDraft = EMPTY_PROVIDER_DRAFT,
  cancelPath = PROVIDERS_PATH,
}: ProviderFormProps) => {
  const navigate = useNavigate();
  const createProvider = useCreateProvider();
  const updateProvider = useUpdateProvider();
  const { draft, setField, toggleProduct, isValid, getProvider, getProviderUpdate } = useProviderForm(initialDraft);

  const { items: datalakes, isLoading: datalakesLoading } = useAllDatalakes();
  const { items: products, isLoading: productsLoading } = useAllImportProducts();
  const [productQuery, setProductQuery] = useState("");

  const isEditing = !!providerId;
  const isMutating = createProvider.isPending || updateProvider.isPending;

  const datalakeItems = useMemo<SearchableDropdownListItem<string>[]>(
    () =>
      datalakes.map((datalake) => ({
        key: datalake.id,
        text: datalake.name,
        searchValue: `${datalake.name} ${datalake.id}`,
      })),
    [datalakes],
  );
  const selectedDatalake = datalakeItems.find((item) => item.key === draft.datalake_id);
  const filteredProducts = useMemo(
    () =>
      productQuery
        ? products.filter((product) => product.name.toLowerCase().includes(productQuery.toLowerCase()))
        : products,
    [productQuery, products],
  );

  const onSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (isMutating || !isValid) {
      return;
    }

    if (providerId) {
      updateProvider.mutate({ id: providerId, provider: getProviderUpdate() });
      return;
    }

    createProvider.mutate(getProvider());
  };

  return (
    <form className="space-y-8 pb-16" onSubmit={onSubmit}>
      <div className="space-y-6">
        <FormField
          id="provider-name"
          label="Name"
          type="text"
          value={draft.name}
          onChangeHandler={(value) => setField("name", value)}
          subtext={NAME_SUBTEXT}
          disabled={isMutating}
          required
        />

        <FormField
          id="provider-id"
          label="Provider ID"
          type="text"
          value={draft.id}
          onChangeHandler={(value) => setField("id", value.slice(0, PROVIDER_ID_MAX_LENGTH))}
          subtext={ID_SUBTEXT}
          disabled={isMutating || isEditing}
          required
        />

        <SearchableDropdown
          label="Datalake"
          items={datalakeItems}
          selectedItem={selectedDatalake}
          setSelectedItem={(item: SearchableDropdownListItem<string>) => setField("datalake_id", item.key)}
          subtext={DATALAKE_SUBTEXT}
          isLoading={datalakesLoading}
          emptyText="No datalakes configured."
          disabled={isMutating || isEditing}
        />

        <fieldset className="space-y-3">
          <legend className="text-sm font-medium text-gray-700">
            Select the Products to which the Provider can stream
          </legend>
          {productsLoading ? (
            <Spinner size="sm" />
          ) : (
            <>
              {products.length > PRODUCT_FILTER_THRESHOLD && (
                <FormField
                  id="provider-product-filter"
                  type="text"
                  value={productQuery}
                  onChangeHandler={setProductQuery}
                  placeholder="Filter products..."
                  disabled={isMutating}
                />
              )}
              {filteredProducts.length === 0 ? (
                <p className="text-sm text-gray-500">
                  {products.length === 0 ? "No products configured." : "No products match this filter."}
                </p>
              ) : (
                filteredProducts.map((product) => (
                  <Checkbox
                    key={product.id}
                    id={`provider-product-${product.id}`}
                    label={
                      <span className="flex items-center gap-2">
                        {product.name}
                        {product.name === SYSTEM_PRODUCT_NAME && <Badge text="system" color={BadgeColor.GRAY} />}
                      </span>
                    }
                    checked={draft.products.includes(product.name)}
                    setChecked={(checked) => toggleProduct(product.name, checked)}
                    disabled={isMutating}
                  />
                ))
              )}
            </>
          )}
        </fieldset>
      </div>

      <div className="flex justify-end gap-3 pt-2">
        <Button type={ButtonStyle.TERTIARY} text="Cancel" onClick={() => navigate(cancelPath)} disabled={isMutating} />
        <Button
          type={ButtonStyle.PRIMARY}
          disabled={isMutating || !isValid}
          submit
          text={
            isMutating ? (
              <div className="flex">
                <Spinner.Inline className="mr-3 text-white" />
                Saving Provider...
              </div>
            ) : (
              "Save Provider"
            )
          }
        />
      </div>
    </form>
  );
};

export default ProviderForm;
